import type {RGBA} from '@Utils/color.util';

export enum ItemTransitionType {
  FADE = 'fade',
  SLIDE = 'slide',
  WIPE = 'wipe',
  DIP_TO_COLOR = 'dipToColor',
}

/** Direction of motion for slide (push) and wipe (reveal edge) transitions. */
export enum SlideTransitionDirection {
  LEFT = 'left',
  RIGHT = 'right',
  UP = 'up',
  DOWN = 'down',
}

export const MIN_TRANSITION_DURATION = 0.1;
export const MAX_TRANSITION_DURATION = 5;
export const DEFAULT_TRANSITION_DURATION = 1;

/**
 * Two boundaries retiled on the millisecond time grid can differ by a float hair; anything
 * beyond this is a genuinely non-adjacent pair (mid-mutation state) and must not render.
 */
export const BOUNDARY_ADJACENCY_EPSILON = 0.011;
export const DEFAULT_DIP_TO_COLOR: RGBA = [0, 0, 0, 1];
export const DEFAULT_SLIDE_DIRECTION = SlideTransitionDirection.LEFT;

/**
 * Identifies a boundary between two consecutive backgrounds on the timeline: the uid of the
 * background BEFORE the gap — whether or not a transition is applied there. Equals the
 * `fromItemUid` of any transition on that boundary. The last background has no boundary after
 * it, so resolvers (`getBoundaryIdForBackground`) return null for it.
 */
export type TransitionBoundaryId = string;

/**
 * A background-to-background transition, keyed by the boundary it plays across.
 *
 * Ownership rule: a transition is OWNED by its `fromItemUid` background — it exists only
 * while that owner exists and has a timeline successor, and `toItemUid` always equals that
 * successor (rewritten by `PageTransitions.revalidate()` after any background mutation).
 * `toItemUid` is stored explicitly so the model generalizes to non-background items later,
 * where "successor" isn't derivable.
 */
interface ItemTransitionBaseObject {
  uid: string;
  fromItemUid: string;
  toItemUid: string;
  duration: number;
}

export interface FadeTransitionObject extends ItemTransitionBaseObject {
  type: ItemTransitionType.FADE;
}

export interface SlideTransitionObject extends ItemTransitionBaseObject {
  type: ItemTransitionType.SLIDE;
  direction: SlideTransitionDirection;
}

export interface WipeTransitionObject extends ItemTransitionBaseObject {
  type: ItemTransitionType.WIPE;
  direction: SlideTransitionDirection;
}

export interface DipToColorTransitionObject extends ItemTransitionBaseObject {
  type: ItemTransitionType.DIP_TO_COLOR;
  color: RGBA;
}

export type ItemTransitionObject = FadeTransitionObject | SlideTransitionObject | WipeTransitionObject | DipToColorTransitionObject;

export interface PageTransitionsObject {
  transitionsHashMap: Record<string, ItemTransitionObject>;
  /**
   * The boundary whose transition is selected on the timeline (`''` = none). Ephemeral UI
   * state (like `selectedAudioItemUID`): included in `toObject()` so the redux mirror
   * survives commits, stripped in `updateFromObject()` so undo/load never restores it.
   */
  selectedBoundaryId: TransitionBoundaryId;
}

/**
 * The time span a transition renders across, centered on the boundary between its two
 * backgrounds. Half-duration is clamped to half of each adjacent span so the in- and
 * out-windows of a short middle background can never overlap.
 */
export interface TransitionWindow {
  startTime: number;
  endTime: number;
  boundaryTime: number;
}

export interface ItemTransitionEndpoints {
  in: ItemTransitionObject | null;
  out: ItemTransitionObject | null;
}
